import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import debounce from 'lodash/debounce';
import React from 'react';
import { EmptyGraphic, List, ListItem, Loading, Section } from '../design-system';
import { includesSearch } from '../utils/search';
import { Empty } from './Empty';
import { ListLabel } from './Label';
import { TextField } from './TextField';

export interface SelectableItem {
  id: string;
  title: string;
  subtitle: string;
  color?: string;
  image?: string;
}

export interface SelectionListProps {
  description?: string;
  error?: string;
  getImage: (item: SelectableItem) => JSX.Element;
  items: SelectableItem[];
  label: string;
  loading?: boolean;
  lowerActions?: JSX.Element;
  noItemsDescription?: string;
  onSelectItem: (item: SelectableItem) => void;
  upperActions?: JSX.Element;
}

const searchItems = (items: SelectableItem[], search: string) =>
  items.filter(({ title, subtitle }) => includesSearch(title, search) || includesSearch(subtitle, search));

const MAX_VISIBLE_ITEMS = 10;
const SEARCH_DEBOUNCE_MS = 200;

export const SelectionList: React.FC<SelectionListProps> = ({
  description,
  error,
  getImage,
  items,
  label,
  loading,
  lowerActions,
  noItemsDescription,
  onSelectItem,
  upperActions,
}: SelectionListProps) => {
  const [search, setSearch] = React.useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');

  const debouncedSetSearch = React.useMemo(
    () => debounce((value: string) => setDebouncedSearch(value), SEARCH_DEBOUNCE_MS),
    [],
  );
  React.useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const onSearchChange = React.useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(target.value);
      debouncedSetSearch(target.value);
    },
    [debouncedSetSearch],
  );
  const onClearSearch = React.useCallback(() => {
    debouncedSetSearch.cancel();
    setSearch('');
    setDebouncedSearch('');
  }, [debouncedSetSearch]);

  const filteredItems = React.useMemo(() => searchItems(items, debouncedSearch), [items, debouncedSearch]);
  const visibleItems = React.useMemo(() => filteredItems.slice(0, MAX_VISIBLE_ITEMS), [filteredItems]);
  const hiddenCount = filteredItems.length - visibleItems.length;

  return (
    <>
      <Section title={`Select ${label}`} description={description}>
        <Box display="flex" alignItems="center">
          <TextField
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="inherit" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton disabled={!search} onClick={onClearSearch} size="small">
                    <ClearIcon color="inherit" fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            autoFocus
            onChange={onSearchChange}
            placeholder={`Search ${label}s`}
            value={search}
          />
          <Box mr="auto" />
          {upperActions}
        </Box>
        {error && (
          <Box pt={2}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}
        {!loading && hiddenCount > 0 && (
          <Box pt={2}>
            <Typography variant="body2" color="textSecondary">
              {`Showing ${visibleItems.length} of ${filteredItems.length} ${label}s. Refine your search to narrow results.`}
            </Typography>
          </Box>
        )}
        <Box pt={2}>
          {loading && <Loading />}
          {!loading &&
            (visibleItems.length > 0 ? (
              <List>
                {visibleItems.map((item) => (
                  <ListItem key={item.id} onClick={() => onSelectItem(item)}>
                    <Box display="flex" flexDirection="row">
                      {getImage(item)}
                      <ListLabel>
                        <Typography variant="body2">{item.title}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          {item.subtitle}
                        </Typography>
                      </ListLabel>
                    </Box>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box mx="auto" my={2} width="50%" textAlign="center">
                <Empty graphic={EmptyGraphic} title={`No ${label}s found`} description={noItemsDescription ?? ''} />
              </Box>
            ))}
        </Box>
      </Section>
      <Box pt={2}>{lowerActions}</Box>
    </>
  );
};
